import { SetStateAction, useMemo, useState } from 'react';

import { generateShortId } from '@lyearn/core/utils/id';

export interface Actions<T> {
  push: (item: T) => string;
  onListInputChange: (item: T, index: number) => void;
  onDelete: (index: number) => void;
  onDuplicate: (index: number) => void;
  setListItems: (nextItems: ListWithTempId<T>[]) => void;
  initializeItems: (nextItems: T[]) => void;
  onMove: (fromIndex: number, toIndex: number) => void;
  setListWithTempId: (value: SetStateAction<ListWithTempId<T>[]>) => void;
  pushMultiple: (items: T[]) => void;
}

export type ListWithTempId<T> = T & {
  tempId: string;
};

export function removeTempId<T>(list: ListWithTempId<T>[]): T[] {
  return list.map(({ tempId, ...item }) => item as unknown as T);
}

function addTempId<T>(list: T[]): ListWithTempId<T>[] {
  return list.map((item) => ({
    ...item,
    tempId: generateShortId(),
  }));
}

const useListWithTempId = <T,>(
  items: T[] = [],
  onChange: (val: T[]) => void,
): [ListWithTempId<T>[], Actions<T>] => {
  const [listWithTempId, setListWithTempId] = useState<ListWithTempId<T>[]>(addTempId(items));

  const actions = useMemo(() => {
    const push = (item: T) => {
      const tempId = generateShortId();

      setListWithTempId((prevList) => {
        const newList = [
          ...prevList,
          {
            ...item,
            tempId,
          },
        ];
        onChange(removeTempId(newList));
        return newList;
      });

      return tempId;
    };

    const pushMultiple = (items: T[]) => {
      setListWithTempId((prevList) => {
        const newList = [
          ...prevList,
          ...items.map((item) => ({
            ...item,
            tempId: generateShortId(),
          })),
        ];
        onChange(removeTempId(newList));
        return newList;
      });
    };

    const onListInputChange = (item: T, index: number) => {
      setListWithTempId((prevList) => {
        const newList = prevList.map((listItem, i) => {
          if (i === index) {
            return { ...listItem, ...item };
          }
          return listItem;
        });
        onChange(removeTempId(newList));
        return newList;
      });
    };

    const onDelete = (index: number) => {
      setListWithTempId((prevList) => {
        const newList = prevList.filter((_, i) => i !== index);
        onChange(removeTempId(newList));
        return newList;
      });
    };
    const onDuplicate = (index: number) => {
      setListWithTempId((prevList) => {
        const duplicateItem = prevList.find((_, i) => i === index);
        if (duplicateItem) {
          const listCopy = [...prevList];
          listCopy.splice(index + 1, 0, {
            ...duplicateItem,
            tempId: generateShortId(),
            id: undefined,
          });
          onChange(removeTempId(listCopy));
          return listCopy;
        }
        return prevList;
      });
    };

    const onMove = (fromIndex: number, toIndex: number) => {
      setListWithTempId((prevList) => {
        if (fromIndex !== toIndex) {
          const listCopy = [...prevList];
          listCopy.splice(toIndex, 0, listCopy.splice(fromIndex, 1)[0]);
          onChange(removeTempId(listCopy));
          return listCopy;
        }
        return prevList;
      });
    };

    const setListItems = (nextItems: ListWithTempId<T>[]) => {
      setListWithTempId(() => {
        onChange(removeTempId(nextItems));
        return nextItems;
      });
    };

    const initializeItems = (nextItems: T[]) => setListWithTempId(addTempId(nextItems));

    return {
      setListWithTempId,
      push,
      onListInputChange,
      onDelete,
      onDuplicate,
      setListItems,
      initializeItems,
      onMove,
      pushMultiple,
    };
  }, [onChange]);

  return [listWithTempId, actions];
};

export default useListWithTempId;
